<script setup lang="ts">
import { IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import { AICONSTANT, ChatStatus, type IChat, AIType } from '@/utils/jDroid'
import Button from '@/components/atoms/button/index.vue'

import { computed, nextTick } from 'vue'
import jdroidService from '@/services/ide/jdroid.service'
import { useIdeStore } from '@/stores/ide.store'
import { useJdroidStore } from '@/stores/jdroid.store'

const ideStore = useIdeStore()
const jdroidStore = useJdroidStore()

const showCPUAndMemory = computed(() => {
  return !ideStore.isCodeExecuting && ideStore.cpuTime && ideStore.memory
})
const showExecuteDetails = computed(() => {
  return !ideStore.isCodeExecuting && ideStore.executionTime
})
const isInteractiveMode = computed(() => {
  return ideStore.interactiveMode
})
const isCompileLanguage = computed(() => {
  return ideStore.ideMeta?.isCompile || ideStore.ideMeta?.language === 'java'
})
const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})
const isCurrentMobileOutputTab = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.OUTPUT
})
const isLastChatLoading = computed(() => {
  return jdroidStore.isLastChatLoading
})
const isLastChatExplainError = computed(() => {
  return jdroidStore.isLastChatExplainError
})

const isGuru = computed(() => {
  return ideStore.isGuru
})

/**
 * Execute with AI
 */
const handleLoginOrDebugWithAi = async () => {
  if (isCurrentMobileOutputTab.value) {
    ideStore.setMobileTab(IDEVIEWTABS_MOBILE.JDROID)
  }
  const chat: IChat = {
    type: AIType.EXPLAINERROR,
    autoReplaceEditor: false,
    ideContent: null,
    outputContent: null,
    prompt: '',
    header: 'Explain Error',
    status: ChatStatus.LOADING,
    response: null
  }
  jdroidService.addChat(chat)
  await new Promise((resolve) => setTimeout(resolve, 200))
  const chatRef = document.getElementById(AICONSTANT.CHAT)
  if (chatRef) {
    await new Promise((resolve) => setTimeout(resolve, 200))
    nextTick(async () => {
      await new Promise((resolve) => setTimeout(resolve, 200))
      chatRef.scrollIntoView({ behavior: 'smooth' })
    })
  }

  //Change status
}
</script>

<template>
  <div>
    <!-- Debug with AI -->
    <div
      v-if="ideStore.hasErrorAfterExecute && !isGuru"
      class="section-primary flex justify-center py-3 sm:py-4"
    >
      <Button
        id="debug-code-with-jdroid-ide"
        variant="filled"
        @click="handleLoginOrDebugWithAi"
        v-if="!isLastChatLoading"
      >
        Debug with JDroid
      </Button>
      <Button
        variant="filled"
        v-if="isLastChatLoading && isLastChatExplainError"
        class="flex gap-2"
      >
        Debugging
        <FontAwesomeIcon spin icon="fa-spinner" class="h-3 w-3" />
      </Button>
    </div>
    <div
      v-if="showCPUAndMemory || showExecuteDetails"
      :class="[
        'p-xsmall flex flex-wrap gap-2 p-4 sm:p-2',
        {
          'justify-center': isFullScreen
        },
        {
          'mx-auto max-w-[400px] justify-center sm:mx-0 sm:max-w-full sm:items-center sm:justify-start':
            !isFullScreen
        }
      ]"
    >
      <FontAwesomeIcon
        icon="fa-circle-info"
        :class="[
          'hidden h-3 w-3',
          {
            'lg:block': !isFullScreen
          }
        ]"
      />
      <p v-show="!isInteractiveMode && showCPUAndMemory" class="">
        CPU Time: <span class="font-semibold">{{ ideStore.cpuTime }} sec(s)</span>
      </p>
      <span v-show="!isInteractiveMode && showCPUAndMemory"> | </span>
      <p v-show="!isInteractiveMode && showCPUAndMemory">
        Memory: <span class="font-semibold">{{ ideStore.memory }} kilobyte(s)</span>
      </p>
      <span
        v-show="!isInteractiveMode && showCPUAndMemory && isCompileLanguage && showExecuteDetails"
        :class="[
          'hidden',
          {
            'sm:block': !isFullScreen
          }
        ]"
      >
        |
      </span>
      <p v-show="isCompileLanguage && showExecuteDetails">
        Compiled and executed in
        <span class="font-semibold">{{ ideStore.executionTime }} sec(s)</span>
      </p>
    </div>
  </div>
</template>
